/**
 * Optional chrome wrapper for content pushed into RightPanel.
 * Provides a sticky header (with close button), a scrollable body, and an optional sticky footer.
 * Content components are free to use this or manage their own layout.
 */
import React from 'react';
import { XIcon } from '@phosphor-icons/react';
import Button from '../ui/Button';
import { useRightPanel } from './RightPanelContext';

interface RightPanelShellProps {
  /** Simple string title (rendered as h2). Ignored when `header` is provided. */
  title?: string;
  /** Rich header content. Takes priority over `title`. */
  header?: React.ReactNode;
  /** Pinned footer rendered below the scrollable body. */
  footer?: React.ReactNode;
  /** Tailwind height class for the header bar (e.g. 'h-[60px]'). Defaults to 'h-auto'. */
  headerHeight?: string;
  children: React.ReactNode;
}

const RightPanelShell: React.FC<RightPanelShellProps> = ({
  title,
  header,
  footer,
  headerHeight = 'h-auto',
  children
}) => {
  const { close } = useRightPanel();

  return (
    <div className="flex flex-col h-full">
      <header
        className={`flex items-center justify-between gap-3 border-b border-border-subtle px-5 shrink-0 ${headerHeight}`}
      >
        <div className="min-w-0 flex-1">
          {header ?? (title ? <h2 className="text-lg font-semibold text-content-default">{title}</h2> : null)}
        </div>
        <Button icon={XIcon} variant="outline" size="sm" aria-label="Close panel" onClick={close} />
      </header>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <footer className="border-t border-border-subtle px-5 py-3 shrink-0">{footer}</footer>}
    </div>
  );
};

export default RightPanelShell;
